import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Circle, MessageCircle, Store, Tag, ExternalLink, Copy, MousePointerClick, AlertTriangle, CheckCircle2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

function daysLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
}

export default function Dashboard() {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [b, o] = await Promise.all([api.get("/business/me"), api.get("/offers")]);
        setBusiness(b.data);
        setOffers(o.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sub = user?.subscription;
  const publicUrl = business ? `${window.location.origin}/b/${business.slug}` : "";
  const totalClicks = offers.reduce((s, o) => s + (o.clicks || 0), 0);

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Page link copied");
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox");
    } catch {
      toast.error("Couldn't send it right now, try again shortly");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline text-primary">Overview</span>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Hi {user?.name?.split(" ")[0]} 👋</h1>
          </div>
          {business && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyLink} className="rounded-full border-white/15" data-testid="copy-link-btn">
                <Copy className="w-4 h-4 mr-1" /> Copy page link
              </Button>
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <Button className="rounded-full bg-primary hover:bg-secondary" data-testid="view-public-btn">
                  <ExternalLink className="w-4 h-4 mr-1" /> View page
                </Button>
              </a>
            </div>
          )}
        </div>

        {/* Email verification banner */}
        {user && user.email_verified === false && (
          <div className="mt-6 rounded-xl border border-white/10 bg-card p-4 flex items-center gap-3" data-testid="verify-email-banner">
            <Mail className="w-5 h-5 text-primary shrink-0" />
            <p className="flex-1 text-sm text-muted-foreground">Verify your email to make sure you never lose access to your account.</p>
            <Button size="sm" variant="outline" onClick={resendVerification} disabled={resending} className="rounded-full border-white/15 shrink-0" data-testid="resend-verification-btn">
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend email"}
            </Button>
          </div>
        )}

        {/* Subscription banner */}
        {sub && (
          <div className={`mt-6 rounded-xl border p-4 flex items-center gap-3 ${sub.status === "active" ? "border-green-500/30 bg-green-500/5" : daysLeft(sub.trial_ends_at) <= 3 ? "border-primary/40 bg-primary/5" : "border-white/10 bg-card"}`} data-testid="subscription-banner">
            {sub.status === "active" ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <AlertTriangle className="w-5 h-5 text-primary" />}
            <div className="flex-1">
              {sub.status === "active" ? (
                <p className="text-sm">Your <span className="font-semibold capitalize">{sub.plan}</span> plan is active · {daysLeft(sub.expires_at)} days left</p>
              ) : (
                <p className="text-sm">Free trial · <span className="font-semibold">{daysLeft(sub.trial_ends_at)} days left</span></p>
              )}
            </div>
            <Link to="/dashboard/billing"><Button size="sm" variant="outline" className="rounded-full border-white/15" data-testid="banner-upgrade-btn">Upgrade</Button></Link>
          </div>
        )}

        {!loading && business && (() => {
          const steps = [
            { done: !!business.logo_path, label: "Add your logo", to: "/dashboard/business" },
            { done: !!business.whatsapp, label: "Add your WhatsApp number so customers can order", to: "/dashboard/business" },
            { done: offers.length > 0, label: "Create your first offer", to: "/dashboard/offers" },
            { done: !!business.published, label: "Publish your page", to: "/dashboard/business" },
          ];
          const left = steps.filter((x) => !x.done);
          if (!left.length) return null;
          return (
            <div className="mt-6 rounded-xl border border-white/10 bg-card p-5" data-testid="onboarding-checklist">
              <p className="font-display font-semibold">Finish setting up ({steps.length - left.length}/{steps.length})</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {steps.map((x) => (
                  <Link key={x.label} to={x.to} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border border-white/10 ${x.done ? "text-muted-foreground line-through" : "hover:border-primary/50"}`}>
                    {x.done ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Circle className="w-4 h-4 text-primary" />} {x.label}
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {!loading && business?.published && (
          <div className="mt-4 rounded-xl border border-white/10 bg-card p-4 flex flex-wrap items-center gap-3 text-sm" data-testid="share-box">
            <span className="text-muted-foreground">Share your page:</span>
            <a className="px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-700 flex items-center gap-1" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`Check out our offers: ${publicUrl}`)}`}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            <a className="px-3 py-1.5 rounded-full border border-white/15 hover:bg-white/5" target="_blank" rel="noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`}>Facebook</a>
            <span className="text-xs text-muted-foreground break-all">Tip: put this link in your WhatsApp Business profile and Instagram bio.</span>
            <div className="w-full flex items-center gap-4 pt-3 border-t border-white/10">
              <div className="bg-white p-2 rounded-lg" data-testid="page-qr"><QRCodeSVG value={publicUrl} size={96} /></div>
              <p className="text-xs text-muted-foreground">Print this QR code for your shop counter or flyers — customers scan it to open your offers.</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {[
                { label: "Offers", value: offers.length, icon: Tag },
                { label: "Active offers", value: offers.filter((o) => o.active).length, icon: CheckCircle2 },
                { label: "Total clicks", value: totalClicks, icon: MousePointerClick },
                { label: "Status", value: business?.published ? "Live" : "Draft", icon: Store },
              ].map((s) => (
                <div key={s.label} className="bg-card border border-white/10 rounded-xl p-5" data-testid={`stat-${s.label}`}>
                  <s.icon className="w-5 h-5 text-primary mb-3" />
                  <div className="font-display text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {!business ? (
              <div className="mt-8 bg-card border border-white/10 rounded-xl p-8 text-center" data-testid="no-business">
                <Store className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold">Set up your business</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Create your business profile to unlock your public page and start listing offers.</p>
                <Link to="/dashboard/business"><Button className="mt-5 rounded-full bg-primary hover:bg-secondary" data-testid="setup-business-btn">Create business profile</Button></Link>
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-6 mt-8">
                <div className="lg:col-span-1 bg-card border border-white/10 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    {business.logo_path ? (
                      <img src={fileUrl(business.logo_path)} alt="logo" className="w-14 h-14 rounded-lg object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-secondary/40 flex items-center justify-center"><Store className="w-6 h-6 text-primary" /></div>
                    )}
                    <div>
                      <h3 className="font-display font-semibold">{business.name}</h3>
                      <p className="text-xs text-muted-foreground">/b/{business.slug}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">{business.tagline || business.description}</p>
                  <Link to="/dashboard/business"><Button variant="outline" size="sm" className="mt-4 rounded-full border-white/15 w-full" data-testid="edit-business-btn">Edit profile</Button></Link>
                </div>

                <div className="lg:col-span-2 bg-card border border-white/10 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-semibold">Recent offers</h3>
                    <Link to="/dashboard/offers"><Button size="sm" variant="ghost" className="text-primary" data-testid="manage-offers-btn">Manage →</Button></Link>
                  </div>
                  {offers.length === 0 ? (
                    <p className="text-sm text-muted-foreground mt-4">No offers yet. Add your first deal!</p>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {offers.slice(0, 5).map((o) => (
                        <div key={o.offer_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0" data-testid={`recent-offer-${o.offer_id}`}>
                          {o.image_path ? <img src={fileUrl(o.image_path)} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-secondary/30" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{o.title}</p>
                            <p className="text-xs text-muted-foreground">{o.discount || o.price}</p>
                          </div>
                          <span className="text-xs text-muted-foreground flex items-center gap-1"><MousePointerClick className="w-3 h-3" />{o.clicks || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
